import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GolfMemberService } from '../../core/services/golf-member.service';
import { debounceTime, distinctUntilChanged, map, Subject, takeUntil } from 'rxjs';
import { SortConfig } from '../../core/interface/SortConfig';
import { PaginationConfig } from '../../core/interface/PaginationConfig';

import {
  ApexChart,
  ApexAxisChartSeries,
  ChartComponent,
  ApexDataLabels,
  ApexPlotOptions,
  ApexYAxis,
  ApexLegend,
  ApexGrid,
  ApexNonAxisChartSeries,
  ApexXAxis
} from "ng-apexcharts";
import { Golf, GolfMember, GolfRevenue, GolfRevenueTotal, GolfRound, MemberType } from '../../core/models/golf-membership'; //TW20250924

export type ChartOptions = {
  series: ApexAxisChartSeries;  // Hapus union type dengan undefined
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  grid: ApexGrid;
  colors: string[];
  legend: ApexLegend;
};

@Component({
  selector: 'app-golf',
  templateUrl: './golf.component.html',
  styleUrl: './golf.component.css'
})
export class GolfComponent implements OnInit, OnDestroy {

  memberItem: Golf[] = []
  displayedData: Golf[] = []
  revenueItem: GolfRevenue[] = []
  ItemMembership: GolfMember = {} as GolfMember
  ItemRound: GolfRound = {} as GolfRound //TW20250924
  ItemTotalRevenue: GolfRevenueTotal = {} as GolfRevenueTotal
  startDate_Renenue: string = ''
  endDate_Revenue: string = ''
  ItemMemberType: MemberType[] = []
  selectedMemberTypeId: number | null = null;

  isLoading: boolean = false
  searchTerm: string = '';
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  startDate: string = '';
  endDate: string = '';
  filter: string = '';
  maxLength: number = 0

  @ViewChild("chart") chart!: ChartComponent;
  public chartOptions!: ChartOptions;
  private resizeListener!: () => void;

  sortConfig: SortConfig = {
    column: '',
    direction: ''
  };

  paginationConfig: PaginationConfig = {
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0
  };

  selectedMemberType: string = 'All Member Type';
  isLoadingTotalRevenue = false;


  constructor(
    private router: Router,
    private golfService: GolfMemberService,
    private route: ActivatedRoute,

  ) {
    this.initChartData([], 0);

    this.setupSearchSubscription();
  }

  private setupSearchSubscription(): void {
    this.searchSubject.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      // Update URL dan load data hanya jika ada term
      if (term.trim()) {
        this.updateUrlParams();
      }
      this.loadMemberData(term);
    });
  }

  ngOnInit(): void {

    this.loadDatesFromLocalStorage();
    this.loadAllData();

    // Pisahkan subscription untuk filter dan parameter lainnya
    this.setupFilterSubscription();
  }

  private loadDatesFromLocalStorage(): void {
    const startDate = localStorage.getItem('startDate');
    const endDate = localStorage.getItem('endDate');

    this.startDate = startDate || '';
    this.endDate = endDate || '';
  }

  private loadAllData(): void {
    // Load semua data master sekali di awal
    this.loadMemberType();
    this.loadGolfRevenue();
    this.loadMembership();
    this.loadRound();   //TW20250925
    this.loadGolfRevenueTotal();
  }

  public refreshDashboardData(): void {

    // Update localStorage jika diperlukan
    if (this.startDate) localStorage.setItem('startDate', this.startDate);
    if (this.endDate) localStorage.setItem('endDate', this.endDate);

    // Reload data
    this.loadAllData();
  }


  private setupFilterSubscription(): void {
    // Subscription untuk filter
    this.route.queryParams
      .pipe(
        takeUntil(this.destroy$),
        map(params => ({
          filter: params['filter'] ? Number(params['filter']) : null,
          search: params['search'] || '',
          page: Number(params['page']) || 1,
          sortColumn: params['sortColumn'] || '',
          sortDirection: params['sortDirection'] || ''
        })),
        distinctUntilChanged((prev, curr) =>
          JSON.stringify(prev) === JSON.stringify(curr)
        )
      )
      .subscribe(params => {
        // Update state dari parameter
        this.searchTerm = params.search;
        this.paginationConfig.currentPage = params.page;
        this.sortConfig.column = params.sortColumn;
        this.sortConfig.direction = params.sortDirection as 'ASC' | 'DESC' | '';

        // Update filter
        const newFilterId = params.filter;
        if (this.selectedMemberTypeId !== newFilterId) {
          this.selectedMemberTypeId = newFilterId;
          this.updateSelectedMemberTypeName();
        }

        // Hanya load member data
        this.loadMemberData(this.searchTerm);
      });
  }

  private updateSelectedMemberTypeName(): void {
    if (this.selectedMemberTypeId !== null && this.ItemMemberType.length > 0) {
      const selectedType = this.ItemMemberType.find(type => type.id === this.selectedMemberTypeId);
      if (selectedType) {
        this.selectedMemberType = selectedType.name;
      } else {
        this.selectedMemberTypeId = null;
        this.selectedMemberType = 'All Member Type';
      }
    } else {
      this.selectedMemberType = 'All Member Type';
    }
  }


  onMemberTypeFilter(id: number, name: string) {
    // console.log('Filtering by:', { id, name });
    this.selectedMemberTypeId = id;
    this.selectedMemberType = name;
    this.updateUrlParams();

    // Hanya load member data saja
    this.loadMemberData(this.searchTerm);
  }

  resetMemberTypeFilter() {
    this.selectedMemberTypeId = null;
    this.selectedMemberType = 'All Member Type';
    this.updateUrlParams();

    // Hanya load member data saja
    this.loadMemberData(this.searchTerm);
  }

  loadMembership(): void {
    this.isLoading = true;

    this.golfService.golfMembership(
      this.startDate,
      this.endDate,
    ).subscribe({
      next: (response) => {
        // Filter duplikat berdasarkan nama
        const uniqueMembers = response.data.members.filter((item, index, self) =>
          index === self.findIndex(t => t.name === item.name)
        );

        this.ItemMembership = {
          ...response.data,
          members: uniqueMembers
        };

        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading revenue data:', error);
        this.isLoading = false;
        this.ItemMembership = {} as GolfMember;
      }
    });
  }

  loadRound(): void {     //TW20250925
    this.isLoading = true;

    this.golfService.golfRound(
      this.startDate,
      this.endDate,
    ).subscribe({
      next: (response) => {
        // Filter duplikat berdasarkan nama
        const uniqueMembers = response.data.members.filter((item, index, self) =>
          index === self.findIndex(t => t.title === item.title)
        );

        this.ItemRound = {
          ...response.data,
          members: uniqueMembers
        };

        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading revenue data:', error);
        this.isLoading = false;
        this.ItemRound = {} as GolfRound;
      }
    });
  }

  trackByMemberName(index: number, item: any): string {
    return item.name;
  }

  loadMemberType(): void {
    this.isLoading = true;

    this.golfService.golfMemberType().subscribe({
      next: (response) => {
        if (Array.isArray(response.data)) {
          this.ItemMemberType = response.data;

          // Set selectedMemberType berdasarkan ID jika ada
          if (this.selectedMemberTypeId !== null) {
            const selectedType = this.ItemMemberType.find(type => type.id === this.selectedMemberTypeId);
            if (selectedType) {
              this.selectedMemberType = selectedType.name;
            } else {
              // Reset jika ID tidak ditemukan
              this.selectedMemberTypeId = null;
              this.selectedMemberType = 'All Member Type';
            }
          }
        } else {
          this.ItemMemberType = [];
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading Member Type data:', error);
        this.isLoading = false;
        this.ItemMemberType = [];
      }
    });
  }

  loadGolfRevenueTotal(): void {
    this.isLoadingTotalRevenue = true;

    this.golfService.golfRevenueTotal(
      this.startDate,
      this.endDate,
    ).subscribe({
      next: (response) => {
        this.ItemTotalRevenue = response.data;
        this.startDate_Renenue = response.meta.start_date;
        this.endDate_Revenue = response.meta.end_date;
        this.maxLength = response.data.max_pct_value;
        this.initChartData(response.data.chart, this.maxLength)
        this.isLoadingTotalRevenue = false;
      },
      error: (error) => {
        console.error('Error loading revenue total data:', error);
        this.isLoading = false;
        this.ItemTotalRevenue = {} as GolfRevenueTotal;
        this.initChartData([], 0); // Reset chart data if error occurs
        this.isLoadingTotalRevenue = false;
      }
    });
  }

  loadGolfRevenue(): void {
    this.isLoading = true;

    this.golfService.golfRevenue(
      this.startDate,
      this.endDate,
    ).subscribe({
      next: (response) => {
        this.revenueItem = response.data;
        // console.log(this.reevenueItem);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading revenue data:', error);
        this.isLoading = false;
        this.revenueItem = [];
      }
    });
  }

  loadMemberData(searchTerm?: string): void {
    this.isLoading = true;

    this.golfService.GetListMemberGolf(
      this.paginationConfig.currentPage,
      this.paginationConfig.itemsPerPage,
      searchTerm?.trim() || '',
      this.sortConfig.column,
      this.sortConfig.direction.toUpperCase() as 'ASC' | 'DESC',
      this.startDate,
      this.endDate,
      this.selectedMemberTypeId
    ).subscribe({
      next: (response) => {
        this.displayedData = response.data;

        // console.log(this.displayedData);
        this.paginationConfig.totalItems = response.pagination.total_items;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading golf data:', error);
        this.isLoading = false;
        this.displayedData = [];
      }
    });
  }

  get totalPages(): number {
    return Math.ceil(this.paginationConfig.totalItems / this.paginationConfig.itemsPerPage);
  }

  get pageNumbers(): number[] {
    const currentPage = this.paginationConfig.currentPage;
    const totalPages = this.totalPages;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  onSearch(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const term = target.value;

    // Update searchTerm dan trigger pencarian
    this.searchTerm = term;
    this.paginationConfig.currentPage = 1;
    this.searchSubject.next(term);

    // Jika input kosong, langsung clear search dan reset data
    if (!term.trim()) {
      const queryParams: Record<string, string | number | null> = {
        page: 1,
        search: null
      };

      // Jika ada sort config, pertahankan
      if (this.sortConfig.column && this.sortConfig.direction) {
        queryParams['sortColumn'] = this.sortConfig.column;
        queryParams['sortDirection'] = this.sortConfig.direction;
      }

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        replaceUrl: true
      });

      this.loadMemberData('');
    }
  }

  onSort(column: string): void {
    if (this.sortConfig.column === column) {
      this.sortConfig.direction = this.sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortConfig.column = column;
      this.sortConfig.direction = 'ASC';
    }

    this.paginationConfig.currentPage = 1; // Reset to first page on sort
    this.updateUrlParams();
    this.loadMemberData(this.searchTerm);
  }

  getSortIcon(column: string): string {
    if (this.sortConfig.column !== column) {
      return 'assets/icons/sort.png';
    }
    if (this.sortConfig.direction === 'ASC') {
      return 'assets/icons/sort.png'; //ASC
    }
    if (this.sortConfig.direction === 'DESC') {
      return 'assets/icons/sort.png'; //DESC
    }
    return 'assets/icons/sort.png';
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.paginationConfig.currentPage) {
      return;
    }

    this.paginationConfig.currentPage = page;
    this.updateUrlParams();
    this.loadMemberData(this.searchTerm);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFirstPage(): void {
    this.onPageChange(1);
  }

  onLastPage(): void {
    this.onPageChange(this.totalPages);
  }

  onPrevPage(): void {
    this.onPageChange(this.paginationConfig.currentPage - 1);
  }

  onNextPage(): void {
    this.onPageChange(this.paginationConfig.currentPage + 1);
  }

  get displayRange(): { start: number; end: number; total: number } {
    const start = (this.paginationConfig.currentPage - 1) * this.paginationConfig.itemsPerPage + 1;
    const end = Math.min(
      start + this.paginationConfig.itemsPerPage - 1,
      this.paginationConfig.totalItems
    );

    return {
      start: this.paginationConfig.totalItems === 0 ? 0 : start,
      end,
      total: this.paginationConfig.totalItems
    };
  }

  private updateUrlParams(): void {
    const queryParams: any = {
      page: this.paginationConfig.currentPage
    };

    // Hanya tambahkan search param jika ada nilai
    if (this.searchTerm?.trim()) {
      queryParams.search = this.searchTerm.trim();
    } else {
      queryParams.search = null; // Hapus search param
    }

    if (this.sortConfig.column && this.sortConfig.direction) {
      queryParams.sortColumn = this.sortConfig.column;
      queryParams.sortDirection = this.sortConfig.direction;
    }

    // Tambahkan parameter memberTypeId jika ada
    if (this.selectedMemberTypeId !== null) {
      queryParams.filter = this.selectedMemberTypeId;
    } else {
      queryParams.filter = null; // Hapus filter param jika tidak ada
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.paginationConfig.currentPage = 1;
    this.updateUrlParams();
    this.loadMemberData('');
  }

  private initChartData(chartData: any[], maxLength: number): void {
    // Ekstrak value untuk series data
    const seriesData = chartData.map(item => item.pct_rev);
    const formattedDates = chartData.map(item => item.period_label);

    // Logika untuk warna - default semua #B6D1C1, yang terakhir #191919
    const xaxisLabelColors = Array(chartData.length).fill("#B6D1C1");
    if (chartData.length > 0) {
      xaxisLabelColors[chartData.length - 1] = "#191919";
    }

    this.chartOptions = {
      series: [{
        name: "Persentase",
        data: seriesData,
      }],
      chart: {
        height: 400,
        width: '100%',
        type: "bar",
        events: {
          click: function (event, chartContext, config) {
            const defaultColors = Array(chartData.length).fill("#B6D1C1");

            if (config.dataPointIndex !== -1) {
              defaultColors[config.dataPointIndex] = "#126936";
            }

            chartContext.updateOptions({
              colors: defaultColors
            }, false, false);
          },

          mouseMove: function (event, chartContext, config) {
            const defaultColors = Array(chartData.length).fill("#B6D1C1");

            if (config.dataPointIndex !== -1) {
              defaultColors[config.dataPointIndex] = "#126936";
            }

            chartContext.updateOptions({
              colors: defaultColors
            }, false, false);
          },
        },
        zoom: {
          enabled: false
        },
        toolbar: {
          show: false
        },
      },
      colors: Array(chartData.length).fill("#B6D1C1"),
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "60%",
          distributed: true,
          dataLabels: {
            position: 'top'
          }
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return val + "%"
        },
        offsetY: -25,
        style: {
          fontSize: '12px',
          colors: ["#126936"],
          fontWeight: 'bold'
        },
        distributed: true,
      },
      legend: {
        show: false
      },
      grid: {
        show: true,
        position: 'back',
        borderColor: '#E6E6E6',
        xaxis: {
          lines: {
            show: false
          },
        },
        yaxis: {
          lines: {
            show: false,
          }
        },
        padding: {
          left: 20
        }
      },
      xaxis: {
        categories: formattedDates,
        labels: {
          rotate: -45,
          trim: false,
          hideOverlappingLabels: false,
          rotateAlways: true,
          style: {
            colors: xaxisLabelColors,
            cssClass: 'apexcharts-xaxis-label',
            fontSize: "12px",
            fontWeight: 'bold'
          },
          offsetY: 8,
          offsetX: 10
        },
        tickPlacement: 'on',
      },
      yaxis: {
        title: {
          text: ""
        },
        min: 0,
        max: maxLength, // Gunakan nilai maksimum yang dihitung
        tickAmount: 5,
        labels: {
          formatter: function (val) {
            return val + "%"
          },
          style: {
            colors: '#666666',
            fontSize: '12px',
            fontWeight: 'normal'
          },
          offsetX: 10
        },
        axisBorder: {
          show: true,
          color: "#E6E6E6"
        },
        axisTicks: {
          show: true,
          color: "#E6E6E6",
          width: 6,
          offsetX: 0
        },
        crosshairs: {
          show: false
        },
        opposite: false
      },
    };
  }

  // Add these methods to your component
  private setupResizeListener(): void {
    window.addEventListener('resize', this.handleChartResize.bind(this));

    // Clean up listener when component is destroyed
    this.ngOnDestroy = () => {
      window.removeEventListener('resize', this.handleChartResize.bind(this));
    };
  }

  private handleChartResize(): void {
    setTimeout(() => {
      const chartContainer = document.querySelector('.chart-container') as HTMLElement;
      const chartWrapper = document.querySelector('.chart-wrapper') as HTMLElement;

      if (chartContainer && chartWrapper) {
        const windowWidth = window.innerWidth;

        // Determine if scrolling is needed based on screen size and content
        if (windowWidth < 768 && this.chartOptions.series[0].data.length > 5) {
          // For mobile with many data points, enable horizontal scrolling
          chartWrapper.classList.add('enable-scroll');
          chartContainer.style.minWidth = (this.chartOptions.series[0].data.length * 70) + 'px';
        } else if (windowWidth >= 768 && windowWidth < 992 && this.chartOptions.series[0].data.length > 7) {
          // For tablets with many data points
          chartWrapper.classList.add('enable-scroll');
          chartContainer.style.minWidth = (this.chartOptions.series[0].data.length * 60) + 'px';
        } else {
          // For desktop or fewer data points, disable scrolling
          chartWrapper.classList.remove('enable-scroll');
          chartContainer.style.minWidth = '100%';
        }
      }
    }, 100);
  }


}
